/**
 * GroupService - Centralized group lifecycle operations.
 *
 * This module owns all standalone group lifecycle operations.
 * It consolidates business logic previously scattered across compat.ts,
 * screen components, and storage effects.
 *
 * Canonical exports (cross-platform parity with Android & iOS):
 *   - addMember(member, group, credentials)
 *   - removeMember(groupId, userId)
 *   - rotateGroupKey(groupId)
 *   - getGroupKeyInfo(groupId)
 *   - getGroupKeyByVersion(groupId, version)
 *   - updateLastMessage(groupId, lastMessage, updatedAt)
 *
 * Parity notes:
 *   - Android: features/groups/GroupRepository.kt (Hilt singleton)
 *   - iOS:     features/groups/GroupService.swift (shared singleton)
 *   - Core:    groups.createGroup, groups.prepareUserAddedMessage (Rust via WASM)
 */

import {
  api as coreApi,
  groups as coreGroups,
  generateUUID,
  MessageType,
  WebSocketEventType,
  stringifyThread,
} from "@/core_bridge/module_api";
import {
  getGroupKeyWithVersion,
  rotateGroupKeyInStorage,
  getGrpKeyByVersion,
  updateGroupLastMessage,
} from "@/services/effects/db/storage";
import { appConfig } from "@/app/module_api";
import { send as sendWebSocket } from "@/transport/webSocketService";
import { encryptDMMessageWithPublicKey } from "@/services/encryption";
import dbStorage from "@/services/effects/db/storage";
import { useStore } from "@/state/store/index";
import { parseJwt } from "@/state/store/slices/auth";
import { resolveContactPublicKey } from "@/data/adapters/contact_keys";
import type { Member, Thread } from "@/data/schemas";

/**
 * Credentials needed for E2E encrypted group operations.
 * Callers pass these in so GroupService stays decoupled from auth state.
 */
export interface GroupCredentials {
  myUserId: string;
  mySecretKey: string;
}

function normalizeThreadKey(groupIdOrThreadKey: string): string {
  if (!groupIdOrThreadKey) return groupIdOrThreadKey;
  return groupIdOrThreadKey.startsWith("group:")
    ? groupIdOrThreadKey
    : `group:${groupIdOrThreadKey}`;
}

function extractGroupId(groupIdOrThreadKey: string): string {
  if (!groupIdOrThreadKey) return groupIdOrThreadKey;
  return groupIdOrThreadKey.startsWith("group:")
    ? groupIdOrThreadKey.slice("group:".length)
    : groupIdOrThreadKey;
}

function resolveThreadFromStore(
  store: any,
  groupIdOrThreadKey: string,
  fallback?: Thread
): Thread | undefined {
  const direct = store.threadMap?.[groupIdOrThreadKey];
  if (direct) return direct as Thread;

  const normalized = normalizeThreadKey(groupIdOrThreadKey);
  const normalizedThread = store.threadMap?.[normalized];
  if (normalizedThread) return normalizedThread as Thread;

  const raw = extractGroupId(groupIdOrThreadKey);
  const rawThread = store.threadMap?.[raw];
  if (rawThread) return rawThread as Thread;

  if (fallback?.id) {
    const fallbackThread = store.threadMap?.[fallback.id];
    if (fallbackThread) return fallbackThread as Thread;
  }

  return fallback;
}

function getSenderCredentials(store: any, credentials?: GroupCredentials): {
  myUserId?: string;
  mySecretKey?: string;
  myPublicKey?: string;
} {
  return {
    mySecretKey:
      credentials?.mySecretKey ||
      store.keyPair?.base64?.secretKey ||
      (store.keyPair as any)?.base64?.secret_key,
    myUserId:
      credentials?.myUserId ||
      parseJwt(store.apiToken || "").user_id,
    myPublicKey:
      store.keyPair?.base64?.publicKey ||
      (store.keyPair as any)?.base64?.public_key,
  };
}

async function resolveRecipientPublicKey(
  store: any,
  receiverId: string,
  myUserId?: string,
  myPublicKey?: string
): Promise<string | undefined> {
  if (receiverId === myUserId && myPublicKey) {
    return String(myPublicKey).replace(/\s+/g, "");
  }

  const local = resolveContactPublicKey(store.contactsById?.[receiverId]);
  if (local) {
    return String(local).replace(/\s+/g, "");
  }

  try {
    const response = await coreApi.searchUserId(receiverId);
    const user = response?.user;
    if (!user) return undefined;

    // Extract public key from response
    let resolvedPublicKey: string | undefined;
    const rawKeys = user.publicKeys || user.public_keys || [];
    if (Array.isArray(rawKeys)) {
      for (const keyEntry of rawKeys) {
        const publicKey = keyEntry?.publicKey ?? keyEntry?.public_key;
        if (publicKey) {
          resolvedPublicKey = String(publicKey).replace(/\s+/g, "");
          break;
        }
      }
    }

    if (!resolvedPublicKey) {
      const publicKey = user.publicKey || user.public_key;
      resolvedPublicKey = publicKey ? String(publicKey).replace(/\s+/g, "") : undefined;
    }

    // FIX: Cache the fetched public key for future use
    if (resolvedPublicKey) {
      try {
        // Update local contact with the fetched public key
        const existingContact = store.contactsById?.[receiverId] || {};
        const updatedContact = {
          ...existingContact,
          userId: receiverId,
          username: user.username || existingContact.username,
          discriminator: user.discriminator || existingContact.discriminator,
          publicKey: resolvedPublicKey,
          publicKeys: rawKeys.length > 0 ? rawKeys : [{ publicKey: resolvedPublicKey, deviceId: "default" }],
          updatedAt: Date.now(),
        };

        // Update in-memory store
        store.setContact?.(updatedContact);

        // Persist to database asynchronously (don't block on this)
        dbStorage.saveContact?.(updatedContact).catch((err: Error) => {
          console.warn("[GroupService] Failed to persist fetched contact:", err);
        });
      } catch (cacheError) {
        console.warn("[GroupService] Failed to cache fetched public key:", cacheError);
        // Continue - we still have the key even if caching failed
      }
    }

    return resolvedPublicKey;
  } catch (error) {
    console.warn("[GroupService] Failed to resolve recipient public key:", receiverId, error);
    return undefined;
  }
}

async function persistThreadLocally(thread: Thread): Promise<void> {
  const store = useStore.getState() as any;
  store.setGroup?.(thread);
  await dbStorage.saveThread?.(thread);
}

function uniqueMembersByUserId(members: Member[]): Member[] {
  const seen = new Set<string>();
  const unique: Member[] = [];
  for (const member of members) {
    if (!member?.userId || seen.has(member.userId)) continue;
    seen.add(member.userId);
    unique.push(member);
  }
  return unique;
}

async function sendThreadUpdatedMessages(params: {
  senderId: string;
  groupId: string;
  groupKey: string;
  groupName?: string;
  groupVersion?: number;
  membershipVersion?: number;
  updatedAt?: number;
  threadKey?: string;
  recipients: Member[];
  credentials?: GroupCredentials;
}): Promise<void> {
  const store = useStore.getState() as any;
  const creds = getSenderCredentials(store, params.credentials);
  const senderId = params.senderId || creds.myUserId;
  if (!senderId) return;

  const lifecycleContent: Record<string, any> = {
    groupKey: params.groupKey,
  };
  if (params.groupName && String(params.groupName).trim()) {
    lifecycleContent.groupName = String(params.groupName).trim();
  }
  if (typeof params.groupVersion === "number" && Number.isFinite(params.groupVersion)) {
    lifecycleContent.groupVersion = params.groupVersion;
  }
  if (
    typeof params.membershipVersion === "number" &&
    Number.isFinite(params.membershipVersion)
  ) {
    lifecycleContent.membershipVersion = params.membershipVersion;
  }
  if (typeof params.updatedAt === "number" && Number.isFinite(params.updatedAt)) {
    lifecycleContent.updatedAt = params.updatedAt;
  }
  if (params.threadKey && String(params.threadKey).trim()) {
    lifecycleContent.threadKey = String(params.threadKey).trim();
  }
  const content = JSON.stringify(lifecycleContent);
  const timestamp = Date.now();

  for (const member of uniqueMembersByUserId(params.recipients || [])) {
    if (!member.userId) continue;

    try {
      const receiverPublicKey = await resolveRecipientPublicKey(
        store,
        member.userId,
        senderId,
        creds.myPublicKey
      );
      if (!receiverPublicKey) {
        console.warn("[GroupService] Missing public key for THREAD_UPDATED recipient:", member.userId);
        continue;
      }

      const encrypted = encryptDMMessageWithPublicKey(
        {
          id: generateUUID(),
          type: MessageType.THREAD_UPDATED,
          contentType: "SYSTEM",
          content,
          receiverId: member.userId,
        },
        receiverPublicKey
      );

      if (!encrypted?.encryptedContent || !encrypted?.nonce) {
        console.warn("[GroupService] Failed to encrypt THREAD_UPDATED for:", member.userId);
        continue;
      }

      sendWebSocket(WebSocketEventType.MESSAGE_SEND, {
        id: encrypted.id || generateUUID(),
        appId: appConfig.appId,
        type: MessageType.THREAD_UPDATED,
        contentType: "SYSTEM",
        senderId,
        receiverId: member.userId,
        groupId: params.groupId,
        encryptedContent: encrypted.encryptedContent,
        nonce: encrypted.nonce,
        status: "SENT",
        createdAt: timestamp,
        updatedAt: timestamp,
      });
    } catch (error) {
      console.warn("[GroupService] Failed to send THREAD_UPDATED to:", member.userId, error);
    }
  }
}

async function sendThreadMemberRemovedMessage(params: {
  senderId: string;
  groupId: string;
  removedUserId: string;
  credentials?: GroupCredentials;
}): Promise<void> {
  const store = useStore.getState() as any;
  const creds = getSenderCredentials(store, params.credentials);
  const senderId = params.senderId || creds.myUserId;
  if (!senderId || !params.removedUserId) return;

  try {
    const receiverPublicKey = await resolveRecipientPublicKey(
      store,
      params.removedUserId,
      senderId,
      creds.myPublicKey
    );
    if (!receiverPublicKey) {
      console.warn(
        "[GroupService] Missing public key for THREAD_MEMBER_REMOVED recipient:",
        params.removedUserId
      );
      return;
    }

    const encrypted = encryptDMMessageWithPublicKey(
      {
        id: generateUUID(),
        type: MessageType.THREAD_MEMBER_REMOVED,
        contentType: "SYSTEM",
        content: JSON.stringify({
          groupId: params.groupId,
          userId: params.removedUserId,
        }),
        receiverId: params.removedUserId,
      },
      receiverPublicKey
    );

    if (!encrypted?.encryptedContent || !encrypted?.nonce) return;

    const timestamp = Date.now();
    sendWebSocket(WebSocketEventType.MESSAGE_SEND, {
      id: encrypted.id || generateUUID(),
      appId: appConfig.appId,
      type: MessageType.THREAD_MEMBER_REMOVED,
      contentType: "SYSTEM",
      senderId,
      receiverId: params.removedUserId,
      groupId: params.groupId,
      encryptedContent: encrypted.encryptedContent,
      nonce: encrypted.nonce,
      status: "SENT",
      createdAt: timestamp,
      updatedAt: timestamp,
    });
  } catch (error) {
    console.warn(
      "[GroupService] Failed to send THREAD_MEMBER_REMOVED to:",
      params.removedUserId,
      error
    );
  }
}

/**
 * Re-shares the group key with a member by emitting a USER_ADDED frame.
 * Exported so peer-assisted recovery can call this when a contact has
 * restored their account and asked us to re-share groups in common.
 */
export async function sendUserAddedMessage(params: {
  thread: Thread;
  newMember: Member;
  senderId: string;
  senderSecretKey: string;
  senderPublicKey?: string;
}): Promise<void> {
  const store = useStore.getState() as any;
  const memberPublicKey = await resolveRecipientPublicKey(
    store,
    params.newMember.userId,
    params.senderId,
    params.senderPublicKey
  );
  if (!memberPublicKey) {
    console.warn(
      "[GroupService] Cannot send USER_ADDED: member has no public key.",
      params.newMember.userId
    );
    return;
  }

  try {
    const threadJson = stringifyThread(params.thread);
    const payload = coreGroups.prepareUserAddedMessage(
      threadJson,
      params.newMember.userId,
      memberPublicKey,
      params.senderId,
      params.senderSecretKey
    );

    sendWebSocket(WebSocketEventType.USER_ADDED, {
      id: payload.id,
      groupId: payload.group_id,
      receiverId: payload.receiver_id,
      senderId: payload.sender_id,
      encryptedKeys: payload.encrypted_keys,
      encryptedGroupData: payload.encrypted_group_data,
      nonce: payload.nonce,
      timestamp: payload.timestamp,
    });
  } catch (error) {
    console.warn("[GroupService] Failed to send USER_ADDED:", error);
  }
}

/**
 * Add a member to a group.
 *
 * 1. Adds the member on the server via API
 * 2. Rotates group key and updates local state
 * 3. Uploads updated encrypted group blob
 * 4. Sends USER_ADDED to the new member
 * 5. Sends THREAD_UPDATED to existing members
 */
export async function addMember(
  member: Member,
  group: Thread,
  credentials?: GroupCredentials
): Promise<void> {
  const store = useStore.getState() as any;
  const currentThread = resolveThreadFromStore(store, group.id, group);
  if (!currentThread) {
    throw new Error("Group thread not found in local state");
  }

  const creds = getSenderCredentials(store, credentials);
  if (!creds.myUserId || !creds.mySecretKey) {
    throw new Error("Missing sender credentials for group member add");
  }

  const threadKey = currentThread.id;
  const groupId = extractGroupId(threadKey);
  const existingMembers = Array.isArray(currentThread.members)
    ? [...currentThread.members]
    : [];

  const alreadyMember = existingMembers.some((m) => m.userId === member.userId);
  if (alreadyMember) return;

  const membersAfterAdd = uniqueMembersByUserId([...existingMembers, member]);
  const rotationInput = {
    ...currentThread,
    members: membersAfterAdd,
    updatedAt: Date.now(),
    version: (Number(currentThread.version) || 0) + 1,
  } as Thread;

  const rotated = coreGroups.rotateGroupKey(rotationInput as any);
  const updatedThread = {
    ...rotated.thread,
    members: membersAfterAdd,
    version: (Number(currentThread.version) || 0) + 1,
    updatedAt: Date.now(),
  } as Thread;

  const newGroupKey =
    rotated.rotation?.newKey ||
    (updatedThread.currentKeyVersion && updatedThread.groupKeys
      ? (updatedThread.groupKeys as any)[updatedThread.currentKeyVersion]
      : undefined);
  if (!newGroupKey) {
    throw new Error("Failed to rotate group key for add member");
  }

  await persistThreadLocally(updatedThread);

  const encryptedThread = coreGroups.encryptThreadForApiWithoutMembers(updatedThread);
  await coreApi.createOrUpdateGroup(encryptedThread);

  await sendUserAddedMessage({
    thread: updatedThread,
    newMember: member,
    senderId: creds.myUserId,
    senderSecretKey: creds.mySecretKey,
    senderPublicKey: creds.myPublicKey,
  });

  await sendThreadUpdatedMessages({
    senderId: creds.myUserId,
    groupId,
    groupKey: newGroupKey,
    groupName: updatedThread.name,
    groupVersion: Number(updatedThread.version || 0) || undefined,
    membershipVersion: membersAfterAdd.length,
    updatedAt: Number(updatedThread.updatedAt || Date.now()),
    threadKey: updatedThread.id || `group:${groupId}`,
    recipients: existingMembers,
    credentials,
  });
}

/**
 * Remove a member from a group.
 *
 * 1. Removes the member on the server via API
 * 2. Rotates group key and updates local state
 * 3. Sends THREAD_MEMBER_REMOVED to removed member
 * 4. Uploads updated encrypted group blob
 * 5. Sends THREAD_UPDATED to remaining members
 */
export async function removeMember(
  groupIdOrThreadKey: string,
  userId: string,
  credentials?: GroupCredentials
): Promise<void> {
  const store = useStore.getState() as any;
  const thread = resolveThreadFromStore(store, groupIdOrThreadKey);
  const threadKey = thread?.id || groupIdOrThreadKey;
  const groupId = extractGroupId(threadKey);

  if (!thread) {
    return;
  }

  const creds = getSenderCredentials(store, credentials);
  if (!creds.myUserId) {
    throw new Error("Missing sender credentials for group member removal");
  }

  const existingMembers = Array.isArray(thread.members) ? [...thread.members] : [];
  const remainingMembers = existingMembers.filter((m) => m.userId !== userId);
  if (remainingMembers.length === existingMembers.length) {
    return;
  }

  const rotationInput = {
    ...thread,
    members: remainingMembers,
    updatedAt: Date.now(),
    version: (Number(thread.version) || 0) + 1,
  } as Thread;

  const rotated = coreGroups.rotateGroupKey(rotationInput as any);
  const updatedThread = {
    ...rotated.thread,
    members: remainingMembers,
    version: (Number(thread.version) || 0) + 1,
    updatedAt: Date.now(),
  } as Thread;

  const newGroupKey =
    rotated.rotation?.newKey ||
    (updatedThread.currentKeyVersion && updatedThread.groupKeys
      ? (updatedThread.groupKeys as any)[updatedThread.currentKeyVersion]
      : undefined);
  if (!newGroupKey) {
    throw new Error("Failed to rotate group key for remove member");
  }

  await persistThreadLocally(updatedThread);

  await sendThreadMemberRemovedMessage({
    senderId: creds.myUserId,
    groupId,
    removedUserId: userId,
    credentials,
  });

  const encryptedThread = coreGroups.encryptThreadForApiWithoutMembers(updatedThread);
  await coreApi.createOrUpdateGroup(encryptedThread);

  await sendThreadUpdatedMessages({
    senderId: creds.myUserId,
    groupId,
    groupKey: newGroupKey,
    groupName: updatedThread.name,
    groupVersion: Number(updatedThread.version || 0) || undefined,
    membershipVersion: remainingMembers.length,
    updatedAt: Number(updatedThread.updatedAt || Date.now()),
    threadKey: updatedThread.id || `group:${groupId}`,
    recipients: remainingMembers,
    credentials,
  });
}

/**
 * Rotate the group encryption key.
 * Generates a new key version while preserving old keys for backward decryption.
 */
export async function rotateGroupKey(groupId: string): Promise<void> {
  await rotateGroupKeyInStorage(groupId);
}

/**
 * Get current group key info.
 * @returns [keyBase64, version] tuple or null if not found
 */
export async function getGroupKeyInfo(
  groupId: string
): Promise<[string, number] | null> {
  return getGroupKeyWithVersion(groupId);
}

/**
 * Get a specific key version for decryption of older messages.
 */
export async function getGroupKeyByVersion(
  groupId: string,
  version: number
): Promise<string | null> {
  return getGrpKeyByVersion(groupId, version);
}

/**
 * Update the last message preview for a group thread.
 */
export async function updateLastMessage(
  groupId: string,
  lastMessage: string,
  updatedAt: number
): Promise<void> {
  await updateGroupLastMessage(groupId, lastMessage, updatedAt);
}
